// Data sources configuration for sanctions screening
// This centralized configuration allows easy management of multiple data sources

const dataSources = {
  EU: {
    id: 'EU',
    name: 'EU Sanctions',
    shortName: 'EU',
    color: 'blue',
    buttonColor: 'bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30',
    badgeColor: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    icon: 'fa-solid fa-landmark-dome',
    linkTemplate: 'https://data.europa.eu/apps/eusanctionstracker/subjects/{{identifier}}',
    linkText: 'View on EU Sanctions Tracker',
    // Special handling for EU travel bans - they don't have individual links
    shouldShowLink: (result) => {
      // If it's a travel ban only (and not also a financial sanction), don't show a link
      return !(result.docData?.dataSources?.includes("travel-bans") && 
             !result.docData?.dataSources?.includes("financial-sanctions"));
    }
  },
  UN: {
    id: 'UN',
    name: 'United Nations Sanctions',
    shortName: 'UN',
    color: 'green',
    buttonColor: 'bg-green-100 hover:bg-green-200 text-green-700 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30',
    badgeColor: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    icon: 'fa-solid fa-globe',
    // UN only has a consolidated list, not individual links
    linkTemplate: 'https://main.un.org/securitycouncil/en/content/un-sc-consolidated-list',
    linkText: 'View on UN Sanctions Consolidated List',
    // Always show the link for UN
    shouldShowLink: () => true
  },
  OFAC: {
    id: 'OFAC',
    name: 'OFAC Sanctions',
    shortName: 'OFAC',
    color: 'red',
    buttonColor: 'bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30',
    badgeColor: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    icon: 'fa-solid fa-flag-usa',
    linkTemplate: 'https://sanctionssearch.ofac.treas.gov/Details.aspx?id={{identifier}}',
    linkText: 'View on OFAC Sanctions List',
    // By default, show the link if logicalId exists
    shouldShowLink: (result) => Boolean(result.docData?.logicalId)
  },
  // Add more data sources as needed
  UK: {
    id: 'UK',
    name: 'UK Sanctions',
    shortName: 'UK',
    color: 'indigo',
    buttonColor: 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400 dark:hover:bg-indigo-900/30',
    badgeColor: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
    icon: 'fa-solid fa-landmark',
    linkTemplate: 'https://sanctionssearch.ukgovdatabaseexample.gov.uk/{{identifier}}',
    linkText: 'View on UK Sanctions Database',
    // By default, show the link if logicalId exists
    shouldShowLink: (result) => Boolean(result.docData?.logicalId)
  }
};

// Helper function to get a data source by ID
export const getDataSource = (sourceId) => {
  return dataSources[sourceId] || {
    id: sourceId,
    name: sourceId,
    shortName: sourceId,
    color: 'gray',
    buttonColor: 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-800/50 dark:text-gray-300 dark:hover:bg-gray-800/70',
    badgeColor: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    icon: 'fa-solid fa-file-lines',
    linkTemplate: '#',
    linkText: `View on ${sourceId}`,
    shouldShowLink: (result) => Boolean(result.docData?.logicalId)
  };
};

// Helper to determine if link should be shown
export const shouldShowSourceLink = (sourceId, result) => {
  const source = getDataSource(sourceId);
  return source.shouldShowLink ? source.shouldShowLink(result) : Boolean(result.docData?.logicalId);
};

// Get URL for a data source
export const getSourceUrl = (sourceId, result) => {
  const source = getDataSource(sourceId);
  const identifier = result.docData?.logicalId || '';
  return source.linkTemplate.replace('{{identifier}}', identifier);
};

// Get all available data sources
export const getAllDataSources = () => {
  return Object.values(dataSources);
};

export default dataSources; 